import { useMemo } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplyIcon from '@mui/icons-material/Reply';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Box, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import {
  DataTable,
  MetricCard,
  PageHeader,
  SectionCard,
  LoadingState,
  StatusChip,
} from '@/components/shared';
import { SmartleadCampaign, SmartleadOverview } from '@/services/smartlead';
import { Column } from '@/trio-ui/DataTable';

export type DatePreset = 'this_month' | 'last_month' | 'this_quarter' | 'this_year' | 'last_year' | 'custom';
export type DateFilter = { preset: DatePreset; startDate: string; endDate: string };

type Props = {
  isConnected: boolean;
  overview: SmartleadOverview;
  isLoading: boolean;
  isSyncing: boolean;
  onSync: () => void;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
};

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Active', color: '#10B981' },
  START: { label: 'Active', color: '#10B981' },
  PAUSED: { label: 'Paused', color: '#F59E0B' },
  STOPPED: { label: 'Stopped', color: '#EF4444' },
  COMPLETED: { label: 'Completed', color: '#71717A' },
  DRAFTED: { label: 'Draft', color: '#71717A' },
  UNKNOWN: { label: 'Unknown', color: '#71717A' },
};

const num = (n: number): string => n.toLocaleString();

const columns: Column<SmartleadCampaign>[] = [
  {
    id: 'name',
    label: 'Campaign',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500, fontSize: 13 }}>{row.name}</Box>
        <Box sx={{ fontSize: 12, color: '#71717A' }}>{num(row.totalLeads)} leads</Box>
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (row) => <StatusChip status={row.status} statusMap={STATUS_MAP} />,
  },
  {
    id: 'sent',
    label: 'Sent',
    align: 'right',
    render: (row) => num(row.sent),
  },
  {
    id: 'opened',
    label: 'Opened',
    align: 'right',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500 }}>{num(row.opened)}</Box>
        <Box sx={{ fontSize: 11, color: '#71717A' }}>{row.openRate}%</Box>
      </Box>
    ),
  },
  {
    id: 'replied',
    label: 'Replied',
    align: 'right',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500, color: row.replied > 0 ? '#10B981' : 'inherit' }}>
          {num(row.replied)}
        </Box>
        <Box sx={{ fontSize: 11, color: '#71717A' }}>{row.replyRate}%</Box>
      </Box>
    ),
  },
  {
    id: 'bounced',
    label: 'Bounced',
    align: 'right',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500, color: row.bounceRate > 5 ? '#EF4444' : 'inherit' }}>
          {num(row.bounced)}
        </Box>
        <Box sx={{ fontSize: 11, color: '#71717A' }}>{row.bounceRate}%</Box>
      </Box>
    ),
  },
  {
    id: 'clicked',
    label: 'Clicked',
    align: 'right',
    render: (row) => num(row.clicked),
  },
];

function formatLastSynced(syncedAt: string | null): string {
  if (!syncedAt) return '';
  const diff = Date.now() - new Date(syncedAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(syncedAt).toLocaleDateString();
}

const CEOxColdEmailComponent = function ({
  isConnected,
  overview,
  isLoading,
  isSyncing,
  onSync,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
}: Props): JSX.Element {
  const dateFilterActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {overview.lastSyncedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
          Synced {formatLastSynced(overview.lastSyncedAt)}
        </Typography>
      )}
      <Tooltip title={isSyncing ? 'Syncing...' : 'Refresh data from Smartlead'}>
        <IconButton
          size="small"
          onClick={onSync}
          disabled={isSyncing}
          sx={{
            color: '#71717A',
            '&:hover': { color: '#054AD9' },
            animation: isSyncing ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
          }}
        >
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <ToggleButtonGroup
        value={dateFilter.preset}
        exclusive
        onChange={(_, val) => val && onPresetChange(val)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontSize: 13,
            px: 1.5,
            py: 0.5,
            borderColor: '#E4E4E7',
            '&.Mui-selected': {
              backgroundColor: '#054AD915',
              color: '#054AD9',
              borderColor: '#054AD9',
              '&:hover': { backgroundColor: '#054AD925' },
            },
          },
        }}
      >
        <ToggleButton value="this_month">This Month</ToggleButton>
        <ToggleButton value="last_month">Last Month</ToggleButton>
        <ToggleButton value="this_quarter">This Quarter</ToggleButton>
        <ToggleButton value="this_year">YTD</ToggleButton>
        <ToggleButton value="last_year">Last Year</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>
      {dateFilter.preset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            type="date"
            size="small"
            value={dateFilter.startDate}
            onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
          <TextField
            type="date"
            size="small"
            value={dateFilter.endDate}
            onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  );
  // --- Funnel chart ---
  const funnelOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => `${p.name}: ${num(p.value)}`,
      },
      series: [
        {
          type: 'funnel',
          left: '10%',
          top: 20,
          bottom: 20,
          width: '80%',
          min: 0,
          max: Math.max(overview.totalSent, 1),
          minSize: '5%',
          maxSize: '100%',
          sort: 'descending',
          gap: 4,
          label: { show: true, position: 'inside', fontSize: 13, fontWeight: 500 },
          data: [
            { value: overview.totalSent, name: 'Sent', itemStyle: { color: '#054AD9' } },
            { value: overview.totalOpened, name: 'Opened', itemStyle: { color: '#6366F1' } },
            { value: overview.totalClicked, name: 'Clicked', itemStyle: { color: '#8B5CF6' } },
            { value: overview.totalReplied, name: 'Replied', itemStyle: { color: '#10B981' } },
          ],
        },
      ],
    }),
    [overview]
  );

  // --- Campaign performance bar chart ---
  const topCampaigns = useMemo(
    () =>
      [...overview.campaigns]
        .filter((c) => c.sent > 0)
        .sort((a, b) => b.replyRate - a.replyRate)
        .slice(0, 10),
    [overview.campaigns]
  );

  const campaignChartOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          params.map((p: any) => `${p.marker} ${p.seriesName}: ${p.value}%`).join('<br/>'),
      },
      legend: {
        data: ['Open Rate', 'Reply Rate'],
        bottom: 0,
        textStyle: { fontSize: 12, color: '#71717A' },
      },
      grid: { left: 140, right: 20, top: 10, bottom: 40 },
      yAxis: {
        type: 'category',
        data: topCampaigns.map((c) =>
          c.name.length > 25 ? `${c.name.slice(0, 25)}...` : c.name
        ),
        axisLabel: { fontSize: 11, color: '#71717A' },
        inverse: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => `${v}%` },
        splitLine: { lineStyle: { color: '#F4F4F5' } },
      },
      series: [
        {
          name: 'Open Rate',
          type: 'bar',
          data: topCampaigns.map((c) => c.openRate),
          itemStyle: { color: '#6366F1', borderRadius: [0, 4, 4, 0] },
          barWidth: 12,
        },
        {
          name: 'Reply Rate',
          type: 'bar',
          data: topCampaigns.map((c) => c.replyRate),
          itemStyle: { color: '#10B981', borderRadius: [0, 4, 4, 0] },
          barWidth: 12,
        },
      ],
    }),
    [topCampaigns]
  );

  if (!isConnected) {
    return (
      <>
        <PageHeader title="Cold Email" actions={dateFilterActions} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE68A',
          }}
        >
          <LinkIcon sx={{ fontSize: 18, color: '#D97706' }} />
          <Box sx={{ fontSize: 13, color: '#92400E', flex: 1 }}>
            Connect your <strong>Smartlead</strong> account to track cold email campaign performance.
          </Box>
          <Link
            to="/settings/integrations"
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Connect now
          </Link>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Cold Email" actions={dateFilterActions} />
        <LoadingState message="Loading campaign data..." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Cold Email" actions={dateFilterActions} />

      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <MetricCard
          label="Emails Sent"
          value={num(overview.totalSent)}
          subtitle={`${overview.totalCampaigns} campaigns (${overview.activeCampaigns} active)`}
          color="#054AD9"
          indicator={<EmailIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Open Rate"
          value={`${overview.avgOpenRate}%`}
          subtitle={`${num(overview.totalOpened)} opened`}
          color="#6366F1"
          indicator={<MarkEmailReadIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Reply Rate"
          value={`${overview.avgReplyRate}%`}
          subtitle={`${num(overview.totalReplied)} replies`}
          color="#10B981"
          indicator={<ReplyIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Bounce Rate"
          value={`${overview.avgBounceRate}%`}
          subtitle={`${num(overview.totalBounced)} bounced`}
          color={overview.avgBounceRate > 5 ? '#EF4444' : '#71717A'}
          indicator={<ReportProblemIcon sx={{ fontSize: 16 }} />}
        />
      </Box>

      {/* Charts */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <SectionCard title="Email Funnel" subtitle="Sent → Opened → Clicked → Replied">
          {overview.totalSent === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#71717A' }}>
              No emails sent yet.
            </Box>
          ) : (
            <ReactECharts option={funnelOption} style={{ height: 280 }} />
          )}
        </SectionCard>

        <SectionCard
          title="Campaign Performance"
          subtitle="Top campaigns by reply rate"
        >
          {topCampaigns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#71717A' }}>
              No campaign data yet.
            </Box>
          ) : (
            <ReactECharts
              option={campaignChartOption}
              style={{ height: Math.max(280, topCampaigns.length * 40 + 60) }}
            />
          )}
        </SectionCard>
      </Box>

      {/* Campaigns table */}
      <DataTable
        columns={columns}
        data={overview.campaigns}
        rowKey={(r) => String(r.id)}
        searchPlaceholder="Search campaigns..."
        searchFields={(r) => r.name}
        emptyMessage="No campaigns found."
      />
    </>
  );
};

export default CEOxColdEmailComponent;
